import React, { useRef, useState } from 'react';
import { IconFile, IconRefresh, IconUpload, IconX } from '@tabler/icons-react';
import { Button } from '@/admin/components/ui/button';
import { Skeleton } from '@/admin/components/ui/skeleton';
import { CodeEditor, detectLanguage } from '@/admin/components/code-editor';
import { FileTreeView } from './file-tree-view';
import { CollapsibleSection } from './collapsible-section';
import { getFilesFromDataTransfer } from './file-utils';
import type { SkillFileInfo } from '@/admin/lib/api';

interface FilesSectionProps {
  activeDir: 'skill' | 'assets';
  onActiveDirChange: (dir: 'skill' | 'assets') => void;
  files: SkillFileInfo[];
  assets: SkillFileInfo[];
  loadingFiles: boolean;
  onRefreshFiles: () => void;
  onUpload: (files: File[]) => Promise<void>;
  uploading: boolean;
  selectedFile: string | null;
  onSelectFile: (path: string) => void;
  fileContent: string | null;
  fileContentType: string | null;
  fileBlobUrl: string | null;
  loadingContent: boolean;
  onSaveFile: () => void;
  savingFile: boolean;
  hasUnsavedChanges: boolean;
  onClosePreview: () => void;
  editedContent: string | null;
  onContentChange: (content: string) => void;
  onDeleteFile: (path: string) => void;
}

export const FilesSection: React.FC<FilesSectionProps> = ({
  activeDir,
  onActiveDirChange,
  files,
  assets,
  loadingFiles,
  onRefreshFiles,
  onUpload,
  uploading,
  selectedFile,
  onSelectFile,
  fileContent,
  fileContentType,
  fileBlobUrl,
  loadingContent,
  onSaveFile,
  savingFile,
  hasUnsavedChanges,
  onClosePreview,
  editedContent,
  onContentChange,
  onDeleteFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const filesToUpload = await getFilesFromDataTransfer(e.dataTransfer);
    if (filesToUpload.length > 0) {
      await onUpload(filesToUpload);
    }
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.length) return;
    const filesToUpload = Array.from(event.target.files);
    await onUpload(filesToUpload);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTreeDrop = async (droppedFiles: File[], targetFolder: string) => {
    const prefixed = targetFolder
      ? droppedFiles.map(
          f =>
            new File([f], `${targetFolder}/${f.name}`, {
              type: f.type,
              lastModified: f.lastModified,
            })
        )
      : droppedFiles;
    await onUpload(prefixed);
  };

  const fileCount = (activeDir === 'skill' ? files : assets).length;

  return (
    <CollapsibleSection
      id="files"
      title="Files & Assets"
      description={`${fileCount} file${fileCount !== 1 ? 's' : ''} in ${activeDir === 'skill' ? 'skill directory' : 'web assets'}`}
      defaultOpen={false}
      headerActions={
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={e => {
            e.stopPropagation();
            onRefreshFiles();
          }}
          disabled={loadingFiles}
        >
          <IconRefresh
            className={`h-3.5 w-3.5 ${loadingFiles ? 'animate-spin' : ''}`}
          />
        </Button>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Left: File Explorer */}
        <div className="flex flex-col border rounded-lg overflow-hidden min-h-[400px]">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
            <span className="text-xs font-medium">Explorer</span>
            <div className="flex bg-muted p-0.5 rounded-lg">
              <button
                onClick={() => onActiveDirChange('skill')}
                className={`px-2 py-0.5 text-[10px] font-medium rounded transition-all ${
                  activeDir === 'skill'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground/80'
                }`}
                title="SKILL.md, scripts, and resources"
              >
                Skill
              </button>
              <button
                onClick={() => onActiveDirChange('assets')}
                className={`px-2 py-0.5 text-[10px] font-medium rounded transition-all ${
                  activeDir === 'assets'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground/80'
                }`}
                title="Web assets (hero, cover)"
              >
                Assets
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {/* Upload Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-3 text-center transition-all cursor-pointer ${
                isDragging
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                  : 'border-muted-foreground/20 hover:border-muted-foreground/40 hover:bg-muted/50'
              } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
              <IconUpload
                className={`h-5 w-5 mx-auto mb-1 ${
                  isDragging ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
              <p className="text-[10px] font-medium">
                {uploading ? 'Uploading...' : 'Drop files or click'}
              </p>
            </div>

            {/* File List */}
            <FileTreeView
              files={activeDir === 'skill' ? files : assets}
              selectedFile={selectedFile}
              onSelect={onSelectFile}
              onDelete={onDeleteFile}
              onDrop={handleTreeDrop}
            />
          </div>
        </div>

        {/* Right: Preview Area */}
        <div className="flex flex-col border rounded-lg overflow-hidden min-h-[400px]">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-xs font-medium truncate">
                {selectedFile || 'Preview'}
              </span>
              {hasUnsavedChanges && (
                <span className="text-[10px] text-amber-600 dark:text-amber-400">
                  (unsaved)
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {selectedFile && fileContent !== null && !fileBlobUrl && (
                <Button
                  variant={hasUnsavedChanges ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={onSaveFile}
                  disabled={savingFile || !hasUnsavedChanges}
                >
                  {savingFile ? 'Saving...' : 'Save'}
                </Button>
              )}
              {selectedFile && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={onClosePreview}
                >
                  <IconX className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            {loadingContent ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-[90%]" />
                <Skeleton className="h-3 w-[95%]" />
                <Skeleton className="h-3 w-[85%]" />
              </div>
            ) : selectedFile && (fileContent !== null || fileBlobUrl) ? (
              <div className="h-full overflow-auto bg-muted/20">
                {fileBlobUrl && fileContentType?.startsWith('image/') ? (
                  <div className="flex items-center justify-center p-4 h-full">
                    <img
                      src={fileBlobUrl}
                      alt={selectedFile}
                      className="max-w-full max-h-full object-contain rounded"
                    />
                  </div>
                ) : fileBlobUrl && fileContentType?.startsWith('video/') ? (
                  <div className="flex items-center justify-center p-4 h-full">
                    <video
                      src={fileBlobUrl}
                      controls
                      className="max-w-full max-h-full rounded"
                    />
                  </div>
                ) : fileBlobUrl && fileContentType?.startsWith('audio/') ? (
                  <div className="flex items-center justify-center p-4 h-full">
                    <audio src={fileBlobUrl} controls />
                  </div>
                ) : (
                  <CodeEditor
                    value={editedContent ?? ''}
                    onChange={onContentChange}
                    language={detectLanguage(selectedFile)}
                    className="h-full"
                  />
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-6 text-center">
                <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                  <IconFile className="h-5 w-5" />
                </div>
                <p className="text-xs">Select a file to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};

